<template>
    <div>
        <v-container grid-list-xs>
            <v-row>
                <v-col>
                    <v-sheet class="pa-5" elevation="3">
                        <PageHeader>
                            <template #header>
                                <h4>{{ $t("plan.review-selection-header", { providerKey: extractedProviderKey }) }}</h4>
                            </template>
                            <template #subheader>
                                {{ $t(`plan.review-selection-subheader`, { providerKey: extractedProviderKey }) }}
                            </template>
                            <template #main>
                                <div class="mt-4">Your Selected Offer</div>
                            </template>
                        </PageHeader>

                        <v-radio-group hide-details :value="paymentPlan">
                            <PaymentPlanOption
                                v-if="paymentPlan"
                                :key="paymentPlan.providerPlanCode"
                                :payment-plan="paymentPlan"
                                :provider-key="extractedProviderKey"
                            />
                        </v-radio-group>

                        <div v-if="isNotPromoPlanAndTermsExist" id="payment-plan-dropdown">
                            <SafeHtmlRenderer :content="paymentPlan.terms[0].body" />
                        </div>

                        <p class="mt-2">Please review your payment details below.</p>

                        <v-row>
                            <v-col cols="12">
                                <h4 id="agreement-header">{{ termMonths }}-Month Agreement</h4>
                                <div id="review-table-subheader">Minimum Payment Plan Terms</div>
                            </v-col>
                            <v-col cols="12" class="pb-0">
                                <v-simple-table id="review-table" class="text-left mb-8 mt-5" dense>
                                    <template #default>
                                        <tbody>
                                            <tr>
                                                <td class="border-none">
                                                    {{ $t(`plan.mpf-review-table.row-1`) }}
                                                </td>
                                                <td class="border-none">
                                                    {{ paymentFrequency }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="border-none">
                                                    {{ $t(`plan.mpf-review-table.row-2`) }}
                                                </td>
                                                <td class="border-none">
                                                    {{ paymentPlan.totalNumberOfPayments }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="border-bottom">
                                                    {{ $t(`plan.mpf-review-table.row-3`) }}
                                                </td>
                                                <td class="border-none">
                                                    {{ paymentPlan.paymentAmountCents | currency }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="border-none">
                                                    {{ $t(`plan.mpf-review-table.row-4`) }}
                                                </td>
                                                <td class="border-none">
                                                    {{ paymentPlan.downPaymentCents | currency }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="border-none">
                                                    {{ $t(`plan.mpf-review-table.row-5`) }}
                                                </td>
                                                <td class="border-none">
                                                    {{ amountFinancedCents | currency }}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="border-none">
                                                    {{ $t(`plan.mpf-review-table.row-6`) }}
                                                </td>
                                                <td class="border-none">{{ apr }} %</td>
                                            </tr>
                                            <tr>
                                                <td class="border-none">
                                                    {{ $t(`plan.mpf-review-table.row-7`) }}
                                                </td>
                                                <td class="border-none">{{ termMonths }} Months</td>
                                            </tr>
                                            <tr>
                                                <td class="border-none">
                                                    {{ $t(`plan.mpf-review-table.row-8`) }}
                                                </td>
                                                <td class="border-none">
                                                    {{ formattedFirstPaymentDueDate }}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </template>
                                </v-simple-table>
                            </v-col>
                        </v-row>

                        <div class="card-actions pb-12 mt-8 ml-1">
                            <v-btn class="card-actions-back" rounded outlined color="primary" @click="back()">
                                {{ $t("action.back") }}
                            </v-btn>
                            <SubmitButton class="card-actions-submit secondary ml-2" @click="submit">
                                {{ $t("action.continue") }}
                            </SubmitButton>
                        </div>
                        <ResponsiveImage :image-url="footerLogo" width="100px" class="footer-logo-center" />
                    </v-sheet>
                </v-col>
            </v-row>
        </v-container>
        <DownPaymentRequiredModal :show-modal="showDownPaymentModal" :amount="downPaymentAmount" />
    </div>
</template>

<script>
    import { applicationState, sessionState } from "@/state"
    import PaymentPlanOption from "@/components/paymentplan/PaymentPlanOption.vue"
    import { formatDate } from "@/util/date"
    import Providers from "@/model/provider/Providers"
    import StipulationViewMixin from "@/components/stipulation/StipulationViewMixin.vue"
    import DownPaymentRequiredModal from "@/components/modal/DownPaymentRequiredModal.vue"
    import dayjs from "dayjs"
    import DynamicProviderKey from "@/model/provider/DynamicProviderKey"
    import ProviderState from "@/state/ProviderState"
    import PayFrequencies from "@/model/apply/PayFrequencies"
    import SafeHtmlRenderer from "@/util/safeHtmlRenderer"

    export default {
        components: {
            DownPaymentRequiredModal,
            PaymentPlanOption,
            SafeHtmlRenderer,
        },
        mixins: [StipulationViewMixin],

        props: {
            mpf: Boolean,
        },

        data() {
            const application = applicationState.application
            const paymentPlan = applicationState.selectedPaymentPlan

            // Hard code date to 37 days from today
            application.firstPaymentDate = dayjs(new Date()).add(37, "day").toDate()

            return {
                providerInfo: Providers[Providers.hfd_standard],
                application,
                paymentPlan,
                sendMobile: false,
                sendEmail: false,
                showDownPaymentModal: false,
                paymentFrequency: PayFrequencies[application.primary.income.payFrequency].label,
            }
        },

        computed: {
            apr() {
                return this.paymentPlan?.promotionalApr?.toFixed(2) || this.paymentPlan.apr?.toFixed(2)
            },

            termMonths() {
                return this.paymentPlan?.promotionalTermMonths || this.paymentPlan.termMonths
            },

            monthlyAmountCents() {
                return this.paymentPlan?.promotionalMonthlyAmountCents || this.paymentPlan.monthlyAmountCents
            },

            isNotPromoPlan() {
                return this.paymentPlan?.typeId !== "promo"
            },

            isNotPromoPlanAndTermsExist() {
                return this.isNotPromoPlan && this.paymentPlan?.terms && this.paymentPlan?.terms[0]?.body !== ""
            },

            amountFinancedCents() {
                return this.paymentPlan.totalCostCents - this.paymentPlan.downPaymentCents
            },

            downPaymentAmount() {
                return applicationState?.application?.downPaymentAmountCents
            },

            extractedProviderKey() {
                return DynamicProviderKey.extractProviderKey(this.paymentPlan.providerKey)
            },

            footerLogo() {
                const response = ProviderState.getPreAuthResponse()
                return Providers[this.extractedProviderKey]?.footerLogo[response.providerProductName]
            },

            formattedFirstPaymentDueDate() {
                return formatDate(applicationState?.application?.firstPaymentDate, "semiVerboseDate")
            },
        },

        methods: {
            onStipulationCompleted() {
                if (this.showDownPaymentModal) {
                    this.next()
                }
            },

            back() {
                this.$router.push(`/providers/${sessionState.providerKey}/additionalContactInformation`)
            },

            async next() {
                applicationState.application.purchaseAmountCents =
                    applicationState.application.tentativePurchaseAmountCents
                const paymentPlan = applicationState.selectedPaymentPlan
                if (paymentPlan.downPaymentCents > 0) {
                    await this.$router.push(`/providers/${sessionState.providerKey}/downpayment`)
                } else {
                    await this.$router.push(`/providers/${sessionState.providerKey}/loanPayment`)
                }
            },

            submit() {
                if (this.isStipulationPending) {
                    this.showDownPaymentModal = true
                } else {
                    this.next()
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
    p {
        color: $primary-color;
    }

    td {
        max-width: 250px;
    }

    table tr {
        border: 1px solid red !important;
    }

    #agreement-header {
        margin-top: to_rem(25px);
        font-size: 22px;
        color: $primary-color;
    }

    #payment-plan-dropdown {
        width: 100%;
        height: 100px;
        margin-top: -25px;
        padding: 12px 16px;

        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        font-weight: bold;
        border-radius: 0px 0px 4px 4px;
        border: 1px solid var(--Aspen-Dental-Neutrals-700, #a7adb0);
        background: var(--Background-Light, #e5f2ff);
    }

    #review-table {
        width: 50%;
    }

    @media only screen and (max-width: 900px) {
        #review-table {
            width: 70%;
        }
    }

    @media only screen and (max-width: 600px) {
        #review-table {
            width: 90%;
        }
    }

    .table-font {
        font-size: to_rem(16px) !important;
    }

    .v-data-table td {
        &:nth-child(1) {
            border-bottom: none;
            font-weight: bold;
            width: 30%;
        }

        &:last-child {
            border-bottom: none;
            text-align: end;
            width: 30%;
        }
    }

    .no-left-border {
        border-left: none !important;
    }

    .no-border {
        border: none !important;
    }

    .no-right-border {
        border-right: none !important;
    }

    .thick-border-top {
        border-top: 1px black solid;
    }

    .no-top-border {
        border-top: none !important;
    }

    .no-bottom-border {
        border-bottom: none !important;
    }

    .agreement-subheader {
        margin-top: to_rem(5px);
        font-size: 18px;
        font-weight: 600;
        color: $primary-color;
    }

    .border-none {
        border: none !important;
    }
</style>
